/**
 * Login Page Component
 * Landing page for unauthenticated users
 * Supports both database and Microsoft/Entra authentication
 */

import { useState, useEffect } from 'react';
import { LogIn, User, Lock } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useAdmin } from '@/context/AdminContext';
import { Button } from '@/components/Common';
import { ErrorMessage } from '@/components/Common';

export function LoginPage() {
  const {
    login,
    loginWithDatabase,
    error,
    isLoading,
    isEntraAuthEnabled,
    isDatabaseAuthEnabled,
  } = useAuth();

  const { config } = useAdmin();
  const { branding } = config;

  // Update document title to match branding
  useEffect(() => {
    document.title = branding.pageTitle || 'Login';
  }, [branding.pageTitle]);

  const [authMode, setAuthMode] = useState<'database' | 'entra'>(
    isDatabaseAuthEnabled ? 'database' : 'entra'
  );
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const handleDatabaseLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await loginWithDatabase(username, password);
    } catch (err) {
      // Error is handled in context
      console.error('Database login failed:', err);
    }
  };

  const handleEntraLogin = async () => {
    try {
      await login();
    } catch (err) {
      // Error is handled in context
      console.error('Entra login failed:', err);
    }
  };

  // Show both authentication methods
  const showBothMethods = isEntraAuthEnabled && isDatabaseAuthEnabled;

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-secondary px-4">
      <div className="w-full max-w-md">
        <div className="rounded-xl bg-bg-primary p-8 shadow-2xl">
          {/* Logo/Header */}
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold text-stellar-primary">
              {branding.headerTitle}
            </h1>
            {branding.headerSubtitle && (
              <p className="mt-2 text-text-secondary">
                {branding.headerSubtitle}
              </p>
            )}
          </div>

          {/* Auth mode tabs (only show if both methods enabled) */}
          {showBothMethods && (
            <div className="mb-6 flex gap-2 rounded-lg bg-bg-secondary p-1">
              <button
                onClick={() => setAuthMode('database')}
                className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                  authMode === 'database'
                    ? 'bg-bg-primary text-text-primary shadow-sm'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                Database
              </button>
              <button
                onClick={() => setAuthMode('entra')}
                className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                  authMode === 'entra'
                    ? 'bg-bg-primary text-text-primary shadow-sm'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                Microsoft
              </button>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="mb-6">
              <ErrorMessage message={error} severity="error" />
            </div>
          )}

          {/* Database Authentication Form */}
          {(authMode === 'database' && isDatabaseAuthEnabled) && (
            <form onSubmit={handleDatabaseLogin} className="space-y-4">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-text-primary mb-2">
                  Username
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-text-tertiary" />
                  <input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-border-default bg-bg-primary text-text-primary focus:border-stellar-primary focus:ring-2 focus:ring-stellar-primary/20 outline-none transition-colors"
                    placeholder="Enter your username"
                    required
                    autoComplete="username"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-text-primary mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-text-tertiary" />
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-border-default bg-bg-primary text-text-primary focus:border-stellar-primary focus:ring-2 focus:ring-stellar-primary/20 outline-none transition-colors"
                    placeholder="Enter your password"
                    required
                    autoComplete="current-password"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                isLoading={isLoading}
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          )}

          {/* Microsoft/Entra Authentication */}
          {(authMode === 'entra' && isEntraAuthEnabled) && (
            <>
              <Button
                onClick={handleEntraLogin}
                variant="primary"
                size="lg"
                isLoading={isLoading}
                leftIcon={<LogIn className="h-5 w-5" />}
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? 'Signing in...' : 'Sign in with Microsoft'}
              </Button>

              {/* Info text */}
              <p className="mt-6 text-center text-xs text-text-tertiary">
                By signing in, you agree to access your Microsoft 365 content
                to provide personalized assistance.
              </p>

              {/* Features list */}
              <div className="mt-8 space-y-3">
                <Feature text="Search across SharePoint and OneDrive" />
                <Feature text="Get AI-powered answers with citations" />
                <Feature text="Upload and analyze documents" />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-text-tertiary">
          {isEntraAuthEnabled
            ? 'Powered by Azure OpenAI and Microsoft Graph'
            : `${branding.headerTitle} - AI Assistant`}
        </p>
      </div>
    </div>
  );
}

function Feature({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2 text-sm text-text-secondary">
      <svg
        className="h-5 w-5 flex-shrink-0 text-success"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 13l4 4L19 7"
        />
      </svg>
      <span>{text}</span>
    </div>
  );
}
